import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedUILanguages, getFlagUrl } from '../i18n';
import { getPreferences, updatePreferences } from '../api/userPreferences';
import { getSidebarData } from '../api/sidebar';
import usePermissions from '../hooks/usePermissions';
import useEntitlements from '../hooks/useEntitlements';
import { isMobileDevice } from '../utils/isMobileDevice';
import './self-learning.css';
import NotificationBell from './NotificationBell';
import SupportNudge from './SupportNudge';
import WidgetHelpLabel from './WidgetHelpLabel';
import AnnouncementBar from './AnnouncementBar';
import SetupHelpBanner from './SetupHelpBanner';

// Eager imports — entry / auth / onboarding surfaces and the default landing.
// These are the first screens a user (and especially a Shopify merchant going
// through install + onboarding) sees, so they must render without a code-split
// loading flash.
import Dashboard from './Dashboard';
import OnboardingPage from './OnboardingPage';
import StandardOnboardingPage from './StandardOnboardingPage';
import WordPressOnboardingPage from './WordPressOnboardingPage';
import LoginPage from './LoginPage';
import SignupPage from './SignupPage';
import AppSumoRegisterPage from './AppSumoRegisterPage';
import AppSumoRedeemPage from './AppSumoRedeemPage';
import AutoLoginPage from './AutoLoginPage';
import DesktopOnlyGate from './DesktopOnlyGate';
import { isFreshSignup, clearFreshSignup } from '../utils/signupGate';
import ResetPasswordPage from './ResetPasswordPage';
import RevertEmailChangePage from './RevertEmailChangePage';
import PlatformSelectionPage from './PlatformSelectionPage';
import { SkillBuilderProvider, useSkillBuilder } from '../context/SkillBuilderContext';
import SkillBuilderRail, { SkillBuilderLauncher, railHiddenFor } from './SkillBuilderRail';

// Lazy imports — every other route is code-split so it loads on demand rather
// than shipping in the initial bundle. This shrinks first paint and keeps the
// heavy pages (and their libraries) out of the critical path (Core Web Vitals).
const WidgetAppearancePage = lazy(() => import('./WidgetAppearancePage'));
const ConversationsPage = lazy(() => import('./AeroChatConversations').then(m => ({ default: m.ConversationsPage })));
const MobileConversationsPage = lazy(() => import('./mobile/MobileConversationsPage'));
const MobileNotificationsPage = lazy(() => import('./mobile/MobileNotificationsPage'));
const ChannelsPage = lazy(() => import('./ChannelsPage'));
const ShopifyPage = lazy(() => import('./ShopifyPage'));
const TestYourBotPage = lazy(() => import('./TestYourBotPage'));
const SettingsPage = lazy(() => import('./SettingsPage'));
const RolesPermissionsPage = lazy(() => import('./RolesPermissionsPage'));
const KnowledgeBasePage = lazy(() => import('./KnowledgeBasePage'));
const CustomInstructionsPage = lazy(() => import('./CustomInstructionsPage'));
const PlaybooksPage = lazy(() => import('./PlaybooksPage'));
const LinkConversionsPage = lazy(() => import('./LinkConversionsPage'));
const AiToolsPage = lazy(() => import('./AiToolsPage'));
const BroadcastsPage = lazy(() => import('./BroadcastsPage'));
const BroadcastWizardPage = lazy(() => import('./BroadcastWizardPage'));
const BroadcastDetailPage = lazy(() => import('./BroadcastDetailPage'));
const TemplateListPage = lazy(() => import('./TemplateListPage'));
const TemplateCreationPage = lazy(() => import('./TemplateCreationPage'));
const SegmentManagementPage = lazy(() => import('./SegmentManagementPage'));
const ContactsPage = lazy(() => import('./ContactsPage'));
const ContactImportWizardPage = lazy(() => import('./ContactImportWizardPage'));
const SelfLearningContainer = lazy(() => import('./SelfLearningContainer'));
const PricingPage = lazy(() => import('./PricingPage'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const WebhooksPage = lazy(() => import('./WebhooksPage'));
const ZapierPage = lazy(() => import('./ZapierPage'));
const MCPPage = lazy(() => import('./MCPPage'));
const DeveloperApiPage = lazy(() => import('./DeveloperApiPage'));
const AirwallexPage = lazy(() => import('./AirwallexPage'));
const PosMalaysiaPage = lazy(() => import('./PosMalaysiaPage'));
const MagentoPage = lazy(() => import('./MagentoPage'));
const WixPage = lazy(() => import('./WixPage'));
const GoogleSheetsPage = lazy(() => import('./GoogleSheetsPage'));
const WordPressPage = lazy(() => import('./WordPressPage'));
import { isAuthenticated, verifySession, logout, getStoredUser, handleLaravelRedirect } from '../api/auth';
import { isShopifyConnected } from '../api/shopify';
import { getOnboardingSteps } from '../api/onboarding';
import { getStandardOnboardingSteps } from '../api/standardOnboarding';
import { getWordpressOnboardingSteps } from '../api/wordpressOnboarding';
import { fetchWordpressFromDb } from '../api/wordpressChannel';
import { fetchWixFromDb } from '../api/wixChannel';
import { fetchTikTokShopAccountsFromDb } from '../api/tiktokShopChannel';
import { subscribeToMemberAssigned } from '../api/socket';
import { connectSocket, disconnectSocket, onConversationCount, onNotification, onAgentStatusChanged, emitAgentStatus, getConversationCount, getAgentStatus } from '../api/realtime';
import { useDesktopNotifications } from '../hooks/useDesktopNotifications';
import { playNotificationBeep } from '../utils/notificationSound';
import aerochatEmblem from '../assets/aerochat_emblem.svg';
import aerochatLogoFull from '../assets/aerochat_logo_full.svg';
import { identifyUser, resetPostHog } from '../analytics/posthog';
import usePostHogPageView from '../hooks/usePostHogPageView';

const BUILD_HASH = typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : 'dev';
const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';

const Icons = {
  Dashboard: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  Conversations: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  KnowledgeBase: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  Widget: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>,
  Channels: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  Shopify: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z"/></svg>,
  WordPress: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.54.82-2.771.82-3.864 0-.405-.026-.78-.07-1.11m-7.981.105c.647-.034 1.231-.102 1.231-.102.582-.064.514-.927-.069-.893 0 0-1.748.137-2.877.137-1.061 0-2.844-.137-2.844-.137-.581-.034-.65.861-.069.893 0 0 .55.068 1.13.102l1.673 4.582-2.348 7.044-3.91-11.626c.647-.033 1.231-.101 1.231-.101.582-.069.514-.927-.069-.893 0 0-1.746.137-2.874.137-.2 0-.438-.005-.69-.013C4.911 3.193 8.235 1.215 12 1.215c2.804 0 5.357 1.072 7.27 2.829-.046-.003-.091-.009-.139-.009-1.061 0-1.812.923-1.812 1.914 0 .89.513 1.643 1.06 2.531.411.72.89 1.643.89 2.977 0 .923-.354 1.994-.82 3.488l-1.075 3.59-3.9-11.61.001.001zM12 22.784c-1.059 0-2.081-.153-3.048-.437l3.237-9.406 3.315 9.087c.024.053.05.101.078.149-1.12.393-2.325.609-3.582.609M1.211 12c0-1.564.336-3.05.935-4.39L7.29 21.709C3.694 19.96 1.212 16.271 1.211 12M12 0C5.385 0 0 5.385 0 12s5.385 12 12 12 12-5.385 12-12S18.615 0 12 0"/></svg>,
  AiTools: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/><path d="M18 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"/></svg>,
  Integrations: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M6 8H5a4 4 0 0 0 0 8h1"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
  Settings: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  Bell: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  User: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  ChevronDown: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  Search: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>,
  Phone: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  Video: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2"/></svg>,
  MoreVertical: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>,
  Paperclip: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>,
  Smile: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/></svg>,
  Send: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>,
  SendSmall: () => <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 2L8 10M16 2L11 16L8 10M16 2L2 7L8 10"/></svg>,
  Close: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 4L4 12M4 4L12 12"/></svg>,
  Upload: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  ChatFilled: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"/></svg>,
  Package: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>,
  ExternalLink: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>,
  UserCheck: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>,
  Ban: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="m4.9 4.9 14.2 14.2"/></svg>,
  PanelRight: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M15 3v18"/></svg>,
  Plus: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>,
  Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  Bot: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>,
  UserIcon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  WhatsApp: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>,
  Messenger: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#0084FF"><path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.912 1.446 5.51 3.708 7.217V22l3.398-1.866c.907.251 1.871.387 2.894.387 5.523 0 10-4.145 10-9.243C22 6.145 17.523 2 12 2z"/></svg>,
  Instagram: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0z"/></svg>,
  Email: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#6b7280"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>,
  WebChat: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  Telegram: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#26A5E4"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121L8.32 13.617l-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.94z"/></svg>,
  Filter: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  CreditCard: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  Logout: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Megaphone: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>,
  FileText: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  Pen: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>,
  Rocket: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>,
  Brain: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44A2.5 2.5 0 0 1 4.5 17.5a2.5 2.5 0 0 1-.44-4.96 2.5 2.5 0 0 1 0-.08A2.5 2.5 0 0 1 4.5 10a2.5 2.5 0 0 1 .96-4.96A2.5 2.5 0 0 1 9.5 2z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44A2.5 2.5 0 0 0 19.5 17.5a2.5 2.5 0 0 0 .44-4.96 2.5 2.5 0 0 0 0-.08A2.5 2.5 0 0 0 19.5 10a2.5 2.5 0 0 0-.96-4.96A2.5 2.5 0 0 0 14.5 2z"/></svg>,
  Users: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Segments: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10"/><path d="M2 12h20"/></svg>,
  Onboarding: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  Magento: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect width="20" height="20" x="2" y="2" rx="4" fill="#f26322"/><text x="12" y="16" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="Arial, sans-serif">M</text></svg>,
  Wix: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect width="20" height="20" x="2" y="2" rx="4" fill="#0c6efc"/><path d="M5 9l2.2 6.5h1.3l1.6-4.6 1.6 4.6H13l2.2-6.5h-1.5l-1.4 4.4L10.9 9H9.7l-1.5 4.4L6.5 9z" fill="#fff"/><rect x="16" y="9" width="1.4" height="6.5" fill="#fff"/><path d="M18.4 9h1.5l1.1 1.9L22.1 9h1.5l-2 3.2 2 3.3h-1.5L21 13.4l-1.1 2.1h-1.5l2-3.3z" fill="#fff" transform="translate(-2.5 0)"/></svg>,
  GoogleSheets: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect width="20" height="20" x="2" y="2" rx="4" fill="#0f9d58"/><rect x="7" y="5.5" width="10" height="13" rx="1" fill="#fff"/><path d="M9 9.5h6M9 12h6M9 14.5h6M12 9.5v5" stroke="#0f9d58" strokeWidth="1.2"/></svg>,
  Conversions: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16"/><rect x="6" y="11" width="3" height="6"/><rect x="11" y="7" width="3" height="10"/><rect x="16" y="13" width="3" height="4"/></svg>,
};

// Map nav item IDs to required permissions (null = always visible)
const navPermissionMap = {
  getstarted: null,
  dashboard: 'dashboard.view',
  conversations: 'conversations.view',
  'knowledge-base': 'knowledge_base.view',
  'self-learning': 'self_learning.view',
  'custom-instructions': 'custom_instructions.view',
  playbooks: 'playbooks.view',
  contacts: 'contacts.view',
  segments: 'contacts.view',
  shopify: 'channels.configure',
  wordpress: 'channels.configure',
  magento: 'channels.configure',
  wix: 'channels.configure',
  'google-sheets': 'channels.configure',
  channels: 'channels.view',
  broadcasts: 'broadcasts.view',
  templates: 'broadcasts.view',
  integrations: 'integrations.view',
  // The AI Tools item (id 'skills') is gated by anyPermissions on the nav item
  // itself (skills.view OR integrations.view) — a single key can't express that.
  widget: 'widget.view',
  settings: 'settings.view',
};

// Integrations detail routes that get the sub-header bar, mapped to the
// heading each one shows there. The names come from integrations:hub.*.title,
// already translated for the cards on the Integrations tab, so a page's name
// can never drift between its card and its own header.
const INTEGRATION_SUBHEADERS = [
  { path: '/integrations/webhooks', titleKey: 'hub.webhooks.title' },
  { path: '/integrations/mcp', titleKey: 'hub.mcp.title' },
  { path: '/integrations/airwallex', titleKey: 'hub.airwallex.title' },
  { path: '/integrations/pos-malaysia', titleKey: 'hub.posMalaysia.title' },
  { path: '/integrations/developer-api', titleKey: 'hub.developerApi.title' },
  // Zapier is deliberately absent: it is not built on IntegrationsKit's
  // DetailPage and carries its own inline back button, so it would end up
  // with two. Its card is withheld from the Integrations grid anyway.
];

const navItems = [
  { sectionKey: 'nav.main', items: [{ id: 'getstarted', labelKey: 'nav.getStarted', icon: 'Rocket' }, { id: 'dashboard', labelKey: 'nav.dashboard', icon: 'Dashboard' }, { id: 'conversations', labelKey: 'nav.conversations', icon: 'Conversations', hasNotification: true }] },
  // Playbooks is intentionally NOT linked from the sidebar; the page is still reachable via the /playbooks URL directly.
  { sectionKey: 'nav.ai', items: [{ id: 'knowledge-base', labelKey: 'nav.knowledgeBase', icon: 'KnowledgeBase' }, { id: 'self-learning', labelKey: 'nav.selfLearning', icon: 'Brain', entitlement: 'self_learning_ai' }, { id: 'custom-instructions', labelKey: 'nav.customInstructions', icon: 'Pen' }, { id: 'link-conversions', labelKey: 'nav.conversions', icon: 'Conversions', entitlement: 'conversion_tracking' }, { id: 'skills', labelKey: 'nav.aiTools', icon: 'AiTools', isNew: true, anyPermissions: ['skills.view', 'integrations.view'] }] },
  { sectionKey: 'nav.crm', items: [{ id: 'contacts', labelKey: 'nav.contacts', icon: 'Users' }, { id: 'segments', labelKey: 'nav.segments', icon: 'Segments' }] },
  { sectionKey: 'nav.messaging', entitlement: 'whatsapp_broadcast', requiresConnection: 'whatsapp', items: [{ id: 'broadcasts', labelKey: 'nav.broadcasts', icon: 'Megaphone' }, { id: 'templates', labelKey: 'nav.templates', icon: 'FileText' }] },
  { sectionKey: 'nav.connect', items: [{ id: 'shopify', labelKey: 'nav.shopify', icon: 'Shopify' }, { id: 'wordpress', labelKey: 'nav.wordpress', icon: 'WordPress' }, { id: 'wix', labelKey: 'nav.wix', icon: 'Wix' }, { id: 'google-sheets', labelKey: 'nav.googleSheets', icon: 'GoogleSheets' }, { id: 'channels', labelKey: 'nav.channels', icon: 'Channels' }] },
  { sectionKey: 'nav.config', items: [{ id: 'widget', labelKey: 'nav.widgetAppearance', icon: 'Widget' }, { id: 'settings', labelKey: 'nav.settings', icon: 'Settings' }] },
];


/* ConversationsPage is imported from AeroChatConversations.jsx (uses GET /chat/backend/api/contacts) */
/* ============================================
   Page Config
   ============================================ */

const pageConfig = {
  dashboard: { titleKey: 'pages.dashboard.title', subtitleKey: 'pages.dashboard.subtitle' },
  conversations: { titleKey: 'pages.conversations.title', subtitleKey: 'pages.conversations.subtitle' },
  'knowledge-base': { titleKey: 'pages.knowledgeBase.title', subtitleKey: 'pages.knowledgeBase.subtitle' },
  shopify: { titleKey: 'pages.shopify.title', subtitleKey: 'pages.shopify.subtitle' },
  channels: { titleKey: 'pages.channels.title', subtitleKey: 'pages.channels.subtitle' },
  skills: { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  'skills/integrations': { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  integrations: { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  'integrations/webhooks': { titleKey: 'pages.webhooks.title', subtitleKey: 'pages.webhooks.subtitle' },
  'integrations/zapier': { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  'integrations/mcp': { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  'integrations/developer-api': { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  'integrations/airwallex': { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  'integrations/pos-malaysia': { titleKey: 'pages.aiTools.title', subtitleKey: 'pages.aiTools.subtitle' },
  widget: { titleKey: 'pages.widget.title', subtitleKey: 'pages.widget.subtitle' },
  settings: { titleKey: 'pages.settings.title', subtitleKey: 'pages.settings.subtitle' },
  'test-bot': { titleKey: 'pages.testBot.title', subtitleKey: 'pages.testBot.subtitle' },
  broadcasts: { titleKey: 'pages.broadcasts.title', subtitleKey: 'pages.broadcasts.subtitle' },
  templates: { titleKey: 'pages.templates.title', subtitleKey: 'pages.templates.subtitle' },
  segments: { titleKey: 'pages.segments.title', subtitleKey: 'pages.segments.subtitle' },
  contacts: { titleKey: 'pages.contacts.title', subtitleKey: 'pages.contacts.subtitle' },
  'contacts/import': { titleKey: 'pages.contactImport.title', subtitleKey: 'pages.contactImport.subtitle' },
  'custom-instructions': { titleKey: 'pages.customInstructions.title', subtitleKey: 'pages.customInstructions.subtitle' },
  playbooks: { titleKey: 'pages.playbooks.title', subtitleKey: 'pages.playbooks.subtitle' },
  'getstarted/shopify': { titleKey: 'pages.getStartedShopify.title', subtitleKey: 'pages.getStartedShopify.subtitle' },
  'self-learning': { titleKey: 'pages.selfLearning.title', subtitleKey: 'pages.selfLearning.subtitle' },
  'link-conversions': { titleKey: 'pages.conversions.title', subtitleKey: 'pages.conversions.subtitle' },
  magento: { titleKey: 'pages.magento.title', subtitleKey: 'pages.magento.subtitle' },
  wix: { titleKey: 'pages.wix.title', subtitleKey: 'pages.wix.subtitle' },
  'google-sheets': { titleKey: 'pages.googleSheets.title', subtitleKey: 'pages.googleSheets.subtitle' },
  wordpress: { titleKey: 'pages.wordpress.title', subtitleKey: 'pages.wordpress.subtitle' },
  subscription: { titleKey: 'pages.subscription.title', subtitleKey: 'pages.subscription.subtitle' },
  'getstarted/standard': { titleKey: 'pages.getStartedStandard.title', subtitleKey: 'pages.getStartedStandard.subtitle' },
  'getstarted/wordpress': { titleKey: 'pages.getStartedWordpress.title', subtitleKey: 'pages.getStartedWordpress.subtitle' },
  profile: { titleKey: 'pages.profile.title', subtitleKey: 'pages.profile.subtitle' },
};

/* ============================================
   Main App Component
   ============================================ */
export default function App() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [currentUser, setCurrentUser] = useState(getStoredUser());
  const [authChecking, setAuthChecking] = useState(true);

  // Verify session on mount + sync interface language from user preferences
  useEffect(() => {
    const checkSession = async () => {
      if (isAuthenticated()) {
        const user = await verifySession();
        if (user) {
          // If company is explicitly flagged for Laravel, redirect there.
          // use_react_admin === false means Laravel; undefined/true means React (backward-compatible).
          if (user.use_react_admin === false) {
            const laravelUrl = import.meta.env.VITE_LARAVEL_ADMIN_URL || 'https://app.aerochat.ai';
            window.location.href = `${laravelUrl}/login`;
            return;
          }
          // If platformType is missing, auto-detect from Shopify connection
          if (!user.platformType) {
            try {
              const { connected } = await isShopifyConnected();
              if (connected) {
                user.platformType = 'shopify';
                // Persist so it sticks across refreshes
                await updatePreferences({ platformType: 'shopify' }).catch(() => {});
                localStorage.setItem('aerochat_user', JSON.stringify(user));
              }
            } catch { /* ignore — will fall through to platform-select */ }
          }
          setCurrentUser(user);
          setIsLoggedIn(true);
          identifyUser(user);
          // Sync interface language from user preferences
          try {
            const prefs = await getPreferences();
            if (prefs?.interfaceLanguage && prefs.interfaceLanguage !== i18n.language) {
              i18n.changeLanguage(prefs.interfaceLanguage);
            }
          } catch { /* use current language */ }
        } else {
          setIsLoggedIn(false);
          setCurrentUser(null);
        }
      }
      setAuthChecking(false);
    };
    checkSession();
  }, []);

  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    setIsLoggedIn(true);
    identifyUser(user);
  };

  const handleLogout = async () => {
    clearFreshSignup();
    try {
      await logout();
      disconnectSocket();
      resetPostHog();
    } catch (err) {
      console.warn('Logout cleanup error:', err);
    } finally {
      window.location.replace('/');
    }
  };

  // Handle /autologin route before auth gate — works for both logged-in and logged-out users
  if (location.pathname === '/autologin') {
    return <AutoLoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Handle /reset-password route before auth gate
  if (location.pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }

  // Handle /revert-email-change before auth gate — lands from the "undo" link
  // mailed to the old address; works logged in or out (the token is the credential).
  if (location.pathname === '/revert-email-change') {
    return <RevertEmailChangePage />;
  }

  // Handle /accept-invite route before auth gate — invited agents land here
  // from the invitation email. Same page as reset-password but with invite
  // copy and auto-login on success.
  if (location.pathname === '/accept-invite') {
    return <ResetPasswordPage mode="invite" onLoginSuccess={handleLoginSuccess} />;
  }

  // Handle /signup route before auth gate
  if (location.pathname === '/signup') {
    if (isLoggedIn) {
      return <Navigate to={currentUser?.platformType ? '/dashboard' : '/platform-select'} replace />;
    }
    return <SignupPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Handle /appsumo/register — AppSumo license redemption (new users only).
  // The AppSumo OAuth callback redirects here with ?license_key=... on first
  // activation. Existing users can't self-serve — they're shown a "contact
  // support" message on the register page. A logged-in visitor is an existing
  // user, so send them to the dashboard.
  if (location.pathname === '/appsumo/register') {
    if (isLoggedIn) {
      return <Navigate to="/dashboard" replace />;
    }
    return <AppSumoRegisterPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Handle /appsumo/redeem — support-shared activation link for EXISTING users.
  // Not linked anywhere in the UI; support sends /appsumo/redeem?license_key=…
  // The page logs the user in and attaches the license in one step, then the
  // normal routing below sends them to onboarding/dashboard.
  if (location.pathname === '/appsumo/redeem') {
    return <AppSumoRedeemPage onLoginSuccess={handleLoginSuccess} />;
  }

  if (authChecking) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#6b7280' }}>{t('status.loading')}</div>;
  }

  if (!isLoggedIn) {
    // Show login for /login or any non-signup route
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // New-signup desktop gate: a brand-new signup on an ACTUAL mobile device must
  // finish onboarding on a desktop. App-wide (before any onboarding route) so it
  // cannot be bypassed by navigating elsewhere. It lifts automatically once
  // onboarding has started (platformType is set, read from the server), so the
  // same phone is unblocked after the user finishes setup on desktop. It never
  // affects desktop signups, or logins / email deep-links — those users already
  // have a platformType (or are routed straight to conversations).
  if (isMobileDevice() && isFreshSignup() && !currentUser?.platformType) {
    return <DesktopOnlyGate onLogout={handleLogout} />;
  }

  // Full-screen platform selection (no sidebar) — shown once after first signup
  if (location.pathname === '/platform-select') {
    if (currentUser?.platformType) {
      // Shopify onboarding lives in the embedded Shopify app, so Shopify
      // merchants skip the main-app onboarding and go straight to the
      // dashboard. Other platforms go to their onboarding page.
      const onboardingRoute = currentUser.platformType === 'shopify'
        ? '/dashboard'
        : currentUser.platformType === 'wordpress'
          ? '/getstarted/wordpress'
          : '/getstarted/standard';
      return <Navigate to={onboardingRoute} replace />;
    }
    return <PlatformSelectionPage currentUser={currentUser} onLoginSuccess={handleLoginSuccess} />;
  }

  // Auto-redirect to platform selection if user hasn't chosen yet
  if (!currentUser?.platformType) {
    return <Navigate to="/platform-select" replace />;
  }

  // The provider sits above AuthenticatedApp, not inside it, because the
  // shell reacts to the rail: opening it collapses the nav sidebar so the
  // page keeps a usable width beside the panel.
  return (
    <SkillBuilderProvider>
      <AuthenticatedApp currentUser={currentUser} onLogout={handleLogout} />
    </SkillBuilderProvider>
  );
}

/* ============================================
   Authenticated App (after login)
   ============================================ */
// Permission-gated route wrapper
function PermGate({ permission, sidebarData, children }) {
  const { canDo } = usePermissions(sidebarData);
  if (!sidebarData) return children; // still loading sidebar, allow through
  return canDo(permission) ? children : <Navigate to="/dashboard" replace />;
}

// Entitlement-gated route wrapper — redirects to /dashboard if the merchant's
// active plan doesn't include the given feature key (e.g. self_learning_ai).
function EntGate({ entitlement, sidebarData, children }) {
  const entitlements = sidebarData?.entitlements || {};
  if (!sidebarData) return children; // still loading sidebar, allow through
  const val = entitlements[entitlement];
  const allowed = typeof val === 'boolean' ? val : !!(val && val.allowed);
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

function AuthenticatedApp({ currentUser, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // The sub-header's own two strings live in the integrations namespace, so its
  // page names are the same ones the Integrations cards already show.
  const { t: tIntegrations } = useTranslation('integrations');
  const integrationSubheader = INTEGRATION_SUBHEADERS.find(
    (entry) => location.pathname === entry.path
  );
  const [collapsed, setCollapsed] = useState(false);
  // Off-canvas nav drawer for small screens (shown via CSS ≤768px). The layout
  // itself is responsive (CSS media queries); this state only opens/closes the
  // drawer — closed automatically on navigation below.
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // On an ACTUAL mobile device (not by window width — so a narrow / split-screen
  // desktop is never affected), the mobile experience is Conversations-only.
  // The redirect is done at RENDER time (via <Navigate> just before the main
  // return) rather than in an effect, so the dashboard never mounts and there's
  // no flash/lag on login.
  const [mobileBlocked] = useState(() => isMobileDevice());

  // While the Workflow agent rail is open, collapse the nav so the page
  // keeps a usable width beside the panel (rail 400px + nav 240px left the
  // Skills page squeezed into three-line buttons). The merchant's own
  // sidebar choice is remembered and put back when the rail closes; if
  // they re-expand the nav while the rail is open, that is respected --
  // this only reacts to the rail opening or closing, never to the nav.
  const { open: railOpen, mode: railMode } = useSkillBuilder();
  const collapsedBeforeRail = useRef(null);
  useEffect(() => {
    if (mobileBlocked || railHiddenFor(location.pathname)) return;
    if (railOpen) {
      if (collapsedBeforeRail.current === null) collapsedBeforeRail.current = collapsed;
      setCollapsed(true);
    } else if (collapsedBeforeRail.current !== null) {
      setCollapsed(collapsedBeforeRail.current);
      collapsedBeforeRail.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railOpen]);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [langSearch, setLangSearch] = useState('');
  const [agentStatus, setAgentStatus] = useState(null);
  const [headerNotification, setHeaderNotificationState] = useState(null);
  // Pages get this as onNotification. Skills, Contacts, MCP and Airwallex send
  // { type: 'success' | 'error' | 'info' | 'warning', message }, a shape the
  // header never rendered (it knows 'toast', 'alert' and 'assignment'), so
  // their messages were silently dropped. Map it onto the toast slot.
  const setHeaderNotification = useCallback((n) => {
    const variant = { success: 'success', error: 'error', info: 'info', warning: 'info' }[n?.type];
    setHeaderNotificationState(variant ? { type: 'toast', variant, message: n.message } : n);
  }, []);
  const [usageAlert, setUsageAlert] = useState(null); // Persistent usage threshold banner
  const [showDesktopNotifBanner, setShowDesktopNotifBanner] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(null); // Per-user notification preferences
  const notifPrefsRef = useRef(null); // Mirror of notifPrefs readable from stable socket handlers
  const langDropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const { isSupported: desktopNotifSupported, permission: desktopNotifPermission, requestPermission: requestDesktopPermission, sendNotification: sendDesktopNotif } = useDesktopNotifications();
  const [sidebarData, setSidebarData] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [onboardingItems, setOnboardingItems] = useState([]);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [standardOnboardingItems, setStandardOnboardingItems] = useState([]);
  const [standardOnboardingLoading, setStandardOnboardingLoading] = useState(true);
  const [wpOnboardingItems, setWpOnboardingItems] = useState([]);
  const [wpOnboardingLoading, setWpOnboardingLoading] = useState(true);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  // Whether the isShopifyConnected() check has RESOLVED. Routes that redirect
  // on !shopifyConnected must wait for this: on a deep-link landing (e.g. the
  // embedded app's "View conversions" → /autologin?redirect=/link-conversions)
  // the route renders before the async check comes back, and treating
  // "not yet known" as "not connected" bounced the merchant to /dashboard —
  // consuming the redirect. Same race EntGate already guards for sidebarData.
  const [shopifyConnChecked, setShopifyConnChecked] = useState(false);
  const [wordpressConnected, setWordpressConnected] = useState(false);
  const [wixConnected, setWixConnected] = useState(false);
  const [tiktokShopConnected, setTiktokShopConnected] = useState(false);
  const [conversationCount, setConversationCount] = useState(0);
  // Increments whenever a relevant in-app notification arrives — tells
  // <NotificationBell /> to refetch its unread count and (if open) list.
  const [notifSignal, setNotifSignal] = useState(0);

  const { canAccess } = useEntitlements(sidebarData);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Track SPA page views in PostHog
  usePostHogPageView();

  // Get active page from URL path
  const rawPage = location.pathname === '/' ? 'dashboard' : location.pathname.slice(1);
  // Map sub-paths to parent for sidebar highlighting and the header title
  // (e.g. /broadcasts/:id, /broadcasts/:id/edit, /templates/new).
  const activePage = rawPage.startsWith('getstarted/') ? 'getstarted'
    // Skills and Integrations (and every integration detail page) now live
    // under the single AI Tools nav entry, whose id is 'skills'.
    : rawPage.startsWith('skills/') ? 'skills'
    : rawPage === 'integrations' || rawPage.startsWith('integrations/') ? 'skills'
    : rawPage.startsWith('contacts/') ? 'contacts'
    : rawPage.startsWith('broadcasts/') ? 'broadcasts'
    : rawPage.startsWith('templates/') ? 'templates'
    : rawPage;

  // Widget config
  const [widgetConfig, setWidgetConfig] = useState({
    brandName: 'AeroChat', primaryColor: '#10b981',
    welcomeMessage: 'Hi there 👋\nHow can we help you today?',
    button1: 'Track my order', button2: 'Promotions & discounts', button3: 'What should I buy?',
    widgetLogo: null, botAvatar: null, agentAvatar: null,
  });

  // Fetch sidebar data on mount
  useEffect(() => {
    const loadSidebarData = async () => {
      setSidebarLoading(true);
      try {
        const data = await getSidebarData();
        setSidebarData(data);
      } catch (err) {
        console.error('Failed to load sidebar data:', err);
        setSidebarData({
          company_name: 'AeroChat',
          company_logo: null,
          plan_name: 'Free Plan',
          user_name: currentUser?.name || 'User',
          user_email: currentUser?.email || 'user@example.com',
          user_logo: currentUser?.avatar || null,
        });
      } finally {
        setSidebarLoading(false);
      }
    };
    loadSidebarData();
  }, []);

  // Check Shopify connection + fetch onboarding steps only if Shopify is connected
  useEffect(() => {
    const load = async () => {
      let connected = false;
      try {
        const result = await isShopifyConnected();
        connected = result.connected;
        setShopifyConnected(connected);
      } catch (err) {
        console.error('Failed to check Shopify connection:', err);
        setShopifyConnected(false);
      } finally {
        setShopifyConnChecked(true);
      }
      if (connected) {
        try {
          const steps = await getOnboardingSteps();
          setOnboardingItems(steps);
        } catch (err) {
          console.error('Failed to load onboarding steps:', err);
        }
      }
      setOnboardingLoading(false);
    };
    load();
  }, []);

  // Check WordPress connection state. Unlike platformType (the static
  // onboarding choice), this reflects whether a WordPress site is actually
  // connected — including when an "other"/standard merchant adds WordPress
  // later via the Channels page. The Knowledge Base uses this to decide
  // whether to surface the synced Products/Pages tabs.
  useEffect(() => {
    let cancelled = false;
    fetchWordpressFromDb()
      .then(r => { if (!cancelled) setWordpressConnected(!!r?.exists); })
      .catch(() => { if (!cancelled) setWordpressConnected(false); });
    return () => { cancelled = true; };
  }, []);

  // Same idea for Wix: the sidebar entry appears for merchants who picked Wix
  // at signup OR who connected a Wix site later from the Channels page.
  useEffect(() => {
    let cancelled = false;
    fetchWixFromDb()
      .then(r => { if (!cancelled) setWixConnected(!!r?.exists); })
      .catch(() => { if (!cancelled) setWixConnected(false); });
    return () => { cancelled = true; };
  }, []);

  // A connected TikTok Shop syncs its products into the knowledge base, so the
  // Knowledge Base page shows the Products tab with a TikTok Shop sync button.
  useEffect(() => {
    let cancelled = false;
    fetchTikTokShopAccountsFromDb()
      .then(r => { if (!cancelled) setTiktokShopConnected((r?.rows || []).some(a => a.active)); })
      .catch(() => { if (!cancelled) setTiktokShopConnected(false); });
    return () => { cancelled = true; };
  }, []);

  // Fetch standard onboarding steps
  useEffect(() => {
    const loadStandardOnboarding = async () => {
      try {
        const steps = await getStandardOnboardingSteps();
        setStandardOnboardingItems(steps);
      } catch (err) {
        console.error('Failed to load standard onboarding steps:', err);
      } finally {
        setStandardOnboardingLoading(false);
      }
    };
    loadStandardOnboarding();
  }, []);

  // Fetch WordPress onboarding steps
  const isWordpress = currentUser?.platformType === 'wordpress';
  useEffect(() => {
    if (!isWordpress) { setWpOnboardingLoading(false); return; }
    const loadWpOnboarding = async () => {
      try {
        const steps = await getWordpressOnboardingSteps();
        setWpOnboardingItems(steps);
      } catch (err) {
        console.error('Failed to load WordPress onboarding steps:', err);
      } finally {
        setWpOnboardingLoading(false);
      }
    };
    loadWpOnboarding();
  }, [isWordpress]);

  // Fetch initial conversation count + agent status
  useEffect(() => {
    const loadInitialData = async () => {
      const [count, status] = await Promise.all([
        getConversationCount(),
        getAgentStatus(),
      ]);
      setConversationCount(count);
      setAgentStatus(status);
    };
    loadInitialData();
  }, []);

  // Close the language + account header dropdowns on any outside mousedown,
  // mirroring NotificationBell.jsx so all three header popups dismiss the
  // same way. Sidebar/route clicks land outside both refs, so navigation
  // also closes them naturally.
  useEffect(() => {
    if (!langDropdownOpen) return;
    const onClick = (e) => {
      if (!langDropdownRef.current?.contains(e.target)) setLangDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [langDropdownOpen]);

  useEffect(() => {
    if (!accountDropdownOpen) return;
    const onClick = (e) => {
      if (!accountDropdownRef.current?.contains(e.target)) setAccountDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [accountDropdownOpen]);

  // Connect socket + listen for realtime events
  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return;

    const unsubCount = onConversationCount((data) => {
      // Filter by company_id if present in payload
      const myCompanyId = getStoredUser()?.company_id;
      const eventCompanyId = data.company_id ?? data.admin_id;
      if (myCompanyId && eventCompanyId && String(eventCompanyId) !== String(myCompanyId)) {
        return;
      }
      setConversationCount(data.count);
    });

    const unsubNotification = onNotification((data) => {
      // Filter by company_id — ignore notifications meant for other companies
      const currentUser = getStoredUser();
      const myCompanyId = currentUser?.company_id;
      const eventCompanyId = data.company_id ?? data.data?.company_id ?? data.data?.admin_id;
      if (myCompanyId && eventCompanyId && String(eventCompanyId) !== String(myCompanyId)) {
        console.log('[Notification] Ignoring event - company_id mismatch:', eventCompanyId, 'expected:', myCompanyId);
        return;
      }

      const isAdmin = (currentUser?.role ?? 99) <= 10;

      // For ticket.transferred, only show to the assigned member
      if (data.event_type === 'ticket.transferred') {
        const assignedTo = data.data?.assigned_to;
        if (assignedTo && currentUser?.id && String(assignedTo) !== String(currentUser.id)) {
          console.log('[Notification] Ignoring ticket.transferred - not assigned to me:', assignedTo, 'my id:', currentUser.id);
          return;
        }
        setNotifSignal((s) => s + 1);
      }

      // conversation.escalated → assigned agent OR admins only
      if (data.event_type === 'conversation.escalated') {
        const assignedTo = data.data?.assigned_to;
        const isAssignedAgent = assignedTo && currentUser?.id && String(assignedTo) === String(currentUser.id);
        if (!isAssignedAgent && !isAdmin) return;
        if (isAssignedAgent) setNotifSignal((s) => s + 1);
      }

      // conversation.resolved → don't notify the person who just resolved;
      // limit to assigned agent or admins
      if (data.event_type === 'conversation.resolved') {
        const resolvedBy = data.data?.resolved_by;
        if (resolvedBy && currentUser?.id && String(resolvedBy) === String(currentUser.id)) return;
        const assignedTo = data.data?.assigned_to;
        const isAssignedAgent = assignedTo && currentUser?.id && String(assignedTo) === String(currentUser.id);
        if (!isAssignedAgent && !isAdmin) return;
      }

      // Apply per-event and master toggles from the user's Profile preferences
      const prefs = notifPrefsRef.current;
      const eventPrefs = prefs?.events?.[data.event_type] || {};
      const desktopAllowed = prefs?.desktopEnabled !== false && eventPrefs.desktop !== false;
      const soundAllowed = prefs?.soundEnabled !== false && eventPrefs.sound !== false;
      if (soundAllowed) playNotificationBeep();
      if (!desktopAllowed) return;

      // Handle usage threshold events as persistent banner
      if (data.event_type && data.event_type.startsWith('usage.threshold.')) {
        setUsageAlert(data);
        // Also fire desktop notification for usage alerts
        sendDesktopNotif(
          t('usageAlert' + (data.data?.threshold || '')),
          t('viewUsage'),
          { tag: 'usage-alert', onClick: () => navigate('/subscription') }
        );
        return;
      }
      setHeaderNotification(data);

      // Fire desktop notification (only when tab hidden — hook handles the check)
      if (data.event_type && data.message) {
        const deepLinkContactId = data.data?.contact_id;
        sendDesktopNotif('AeroChat', data.message, {
          tag: data.event_type,
          onClick: () => {
            if (data.event_type === 'conversation.escalated' || data.event_type === 'ticket.transferred') {
              const target = deepLinkContactId
                ? `/conversations?contactId=${deepLinkContactId}`
                : '/conversations';
              navigate(target);
            }
          }
        });
      }
    });

    const unsubAgentStatus = onAgentStatusChanged((data) => {
      // Filter by company_id if present in payload
      const myCompanyId = getStoredUser()?.company_id;
      const eventCompanyId = data.company_id ?? data.admin_id;
      if (myCompanyId && eventCompanyId && String(eventCompanyId) !== String(myCompanyId)) {
        return;
      }
      setAgentStatus(data.status);
    });

    return () => {
      if (unsubCount) unsubCount();
      if (unsubNotification) unsubNotification();
      if (unsubAgentStatus) unsubAgentStatus();
    };
  }, []);

  // Listen for member_assigned socket events and show notification + sound
  useEffect(() => {
    const unsubMemberAssigned = subscribeToMemberAssigned((payload) => {
      const prefs = notifPrefsRef.current;
      const eventPrefs = prefs?.events?.['ticket.transferred'] || {};
      const desktopAllowed = prefs?.desktopEnabled !== false && eventPrefs.desktop !== false;
      const soundAllowed = prefs?.soundEnabled !== false && eventPrefs.sound !== false;

      if (desktopAllowed) {
        // Show header notification immediately
        setHeaderNotification({
          type: 'assignment',
          contactId: payload.contact_id,
          message: t('header.newTicketAssigned'),
        });

        // Fire desktop notification for ticket assignment
        sendDesktopNotif(
          'AeroChat',
          t('header.newTicketAssigned'),
          {
            tag: 'ticket-assigned',
            onClick: () => navigate(payload.contact_id ? `/conversations?contactId=${payload.contact_id}` : '/conversations'),
          }
        );
      }

      if (!soundAllowed) return;
      playNotificationBeep();
    });

    return () => {
      if (unsubMemberAssigned) unsubMemberAssigned();
    };
  }, []);

  // Auto-dismiss toasts
  useEffect(() => {
    if (headerNotification?.type === 'toast' || headerNotification?.type === 'assignment') {
      const delay = headerNotification.type === 'assignment' ? 12000 : 3000;
      const timer = setTimeout(() => setHeaderNotificationState(null), delay);
      return () => clearTimeout(timer);
    }
  }, [headerNotification]);

  // Load per-user notification preferences on mount
  useEffect(() => {
    getPreferences().then(res => {
      if (res?.notifications) {
        setNotifPrefs(res.notifications);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    notifPrefsRef.current = notifPrefs;
  }, [notifPrefs]);

  // Show desktop notification permission banner (non-intrusive)
  useEffect(() => {
    if (!desktopNotifSupported) return;
    if (desktopNotifPermission !== 'default') return; // Already granted or denied

    // Track session count in localStorage
    const countKey = 'aerochat_session_count';
    const dismissKey = 'aerochat_notif_banner_dismissed';
    const count = parseInt(localStorage.getItem(countKey) || '0', 10) + 1;
    localStorage.setItem(countKey, String(count));

    const dismissCount = parseInt(localStorage.getItem(dismissKey) || '0', 10);
    // Show after 3+ sessions, stop showing after 3 dismissals
    if (count >= 3 && dismissCount < 3) {
      setShowDesktopNotifBanner(true);
    }
  }, [desktopNotifSupported, desktopNotifPermission]);

  const currentPage = pageConfig[rawPage] || pageConfig[activePage] || pageConfig.dashboard;
  const setupComplete = !shopifyConnected || onboardingItems.length === 0 || onboardingItems.filter(i => i.type === 'required').every(i => i.status === 'completed');
  const setupAlmost = onboardingItems.length > 0 && onboardingItems.filter(i => i.status === 'completed' || i.status === 'skipped').length >= 4;
  const stdOnboardingComplete = standardOnboardingItems.filter(i => i.type === 'required').every(i => i.status === 'completed');
  const stdOnboardingAlmost = standardOnboardingItems.filter(i => i.status === 'completed' || i.status === 'skipped').length >= 4;
  const wpOnboardingComplete = wpOnboardingItems.filter(i => i.type === 'required').every(i => i.status === 'completed');
  const wpOnboardingAlmost = wpOnboardingItems.filter(i => i.status === 'completed' || i.status === 'skipped').length >= 4;
  const onboardingComplete = shopifyConnected ? setupComplete : isWordpress ? wpOnboardingComplete : stdOnboardingComplete;
  // Setup-help banner follows the Get Started nav item: shown while onboarding is
  // incomplete (never for Shopify, whose onboarding lives in the embedded app),
  // and it holds back the AnnouncementBar. Both wait for onboarding status so
  // neither flashes in before the other on load.
  const onboardingStatusLoaded = !onboardingLoading && !standardOnboardingLoading && !wpOnboardingLoading;
  const showSetupHelp = onboardingStatusLoaded && !mobileBlocked && !shopifyConnected && !onboardingComplete;
  const companyName = sidebarData?.company_name || '';
  const companyLogo = sidebarData?.company_logo;
  const planName = sidebarData?.plan_name || '';
  const userName = sidebarData?.user_name || '';
  const userLogo = sidebarData?.user_logo;

  // Mobile = Conversations-only. Redirect at render time (synchronous) so the
  // dashboard route never mounts — avoids the brief dashboard flash on login.
  if (mobileBlocked && !location.pathname.startsWith('/conversations')) {
    return <Navigate to="/conversations" replace />;
  }

  return (
    <div className="admin-layout">
      {/* Off-canvas drawer scrim (narrow desktop windows only; on a mobile
          device the sidebar is not rendered and the hamburger is disabled). */}
      {drawerOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setDrawerOpen(false)} />
      )}
      {/* Sidebar — hidden on mobile devices (Conversations-only experience). */}
      {!mobileBlocked && (
      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${drawerOpen ? 'sidebar--drawer-open' : ''}`}>
        <div className="sidebar__header" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} style={{ cursor: 'pointer' }}>
          {collapsed ? (
            <img src={aerochatEmblem} alt="AeroChat" className="sidebar__logo-emblem" />
          ) : (
            <img src={aerochatLogoFull} alt="AeroChat" className="sidebar__logo-full" />
          )}
        </div>
        <nav className="sidebar__nav">
          {navItems.map(section => {
            // Hide entire section if it requires an entitlement the plan doesn't have
            if (section.entitlement && !canAccess(section.entitlement)) return null;
            // Hide entire section if it requires a channel connection the merchant hasn't made
            // (e.g. Broadcasts needs a connected WhatsApp Business Account via Channels → Embedded Signup).
            if (section.requiresConnection === 'whatsapp' && !sidebarData?.whatsapp_connected) return null;
            const userPerms = sidebarData?.permissions || {};
            const userLevel = sidebarData?.hierarchy_level ?? 99;
            const visibleItems = section.items.filter(item => {
              if (item.id === 'shopify' && !shopifyConnected) return false;
              // Conversions is a Shopify-only feature — only surface it once a
              // Shopify store is connected (in addition to the plan entitlement).
              if (item.id === 'link-conversions' && !shopifyConnected) return false;
              // WordPress is a platform integration, not a messaging channel —
              // only surface it for merchants who picked WordPress as their platform.
              if (item.id === 'wordpress' && !isWordpress) return false;
              // Wix likewise: shown for Wix-platform merchants, or once a Wix
              // site is actually connected (other platforms connect it from
              // the Channels page).
              if (item.id === 'wix' && currentUser?.platformType !== 'wix' && !wixConnected) return false;
              // Google Sheets is the store for sellers with no e-commerce
              // platform; a Shopify store already has its own catalog and orders.
              if (item.id === 'google-sheets' && shopifyConnected) return false;
              // Shopify merchants do their onboarding in the embedded Shopify
              // app, so the main-app Get Started item is hidden for them; other
              // platforms hide it only once onboarding is complete.
              if (item.id === 'getstarted' && (shopifyConnected || onboardingComplete)) return false;
              // Per-item entitlement gating (e.g. self-learning needs self_learning_ai)
              if (item.entitlement && !canAccess(item.entitlement)) return false;
              // Permission-based filtering
              // Items reachable through more than one permission (AI Tools =
              // Skills OR Integrations) list them here instead of in navPermissionMap.
              if (item.anyPermissions && userLevel > 10 && !item.anyPermissions.some(p => userPerms[p])) return false;
              const requiredPerm = navPermissionMap[item.id];
              if (requiredPerm && userLevel > 10 && !userPerms[requiredPerm]) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;
            return (
            <div key={section.sectionKey} className="nav-section">
              <div className="nav-section__title">{t(section.sectionKey)}</div>
              {visibleItems.map(item => {
                const IC = Icons[item.icon];
                return (
                  <button key={item.id} className={`nav-item ${activePage === item.id ? 'nav-item--active' : ''}`} onClick={() => navigate(item.id === 'getstarted' ? (shopifyConnected ? '/getstarted/shopify' : isWordpress ? '/getstarted/wordpress' : '/getstarted/standard') : `/${item.id}`)} title={collapsed ? t(item.labelKey) : undefined}>
                    <span className="nav-item__icon">
                      <IC />
                      {item.hasNotification && collapsed && conversationCount > 0 && <span className="nav-item__notification-dot" />}
                      {item.id === 'getstarted' && collapsed && shopifyConnected && !setupComplete && <span className={`nav-item__setup-dot ${setupAlmost ? 'nav-item__setup-dot--almost' : ''}`} />}
                      {item.id === 'getstarted' && collapsed && !shopifyConnected && isWordpress && !wpOnboardingComplete && <span className={`nav-item__setup-dot ${wpOnboardingAlmost ? 'nav-item__setup-dot--almost' : ''}`} />}
                      {item.id === 'getstarted' && collapsed && !shopifyConnected && !isWordpress && !stdOnboardingComplete && <span className={`nav-item__setup-dot ${stdOnboardingAlmost ? 'nav-item__setup-dot--almost' : ''}`} />}
                    </span>
                    <span className="nav-item__label">{t(item.labelKey)}</span>
                    {item.hasNotification && !collapsed && conversationCount > 0 && <span className="nav-item__badge badge badge--danger">{conversationCount}</span>}
                    {item.isNew && !collapsed && <span className="nav-item__new-pill">{t('status.new')}</span>}
                    {item.id === 'getstarted' && shopifyConnected && !setupComplete && !collapsed && <span className={`nav-item__setup-pill ${setupAlmost ? 'nav-item__setup-pill--almost' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/getstarted/shopify'); }}>{setupAlmost ? t('status.almostThere') : t('status.incomplete')}</span>}
                    {item.id === 'getstarted' && !shopifyConnected && isWordpress && !wpOnboardingComplete && !collapsed && <span className={`nav-item__setup-pill ${wpOnboardingAlmost ? 'nav-item__setup-pill--almost' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/getstarted/wordpress'); }}>{wpOnboardingAlmost ? t('status.almostThere') : t('status.incomplete')}</span>}
                    {item.id === 'getstarted' && !shopifyConnected && !isWordpress && !stdOnboardingComplete && !collapsed && <span className={`nav-item__setup-pill ${stdOnboardingAlmost ? 'nav-item__setup-pill--almost' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/getstarted/standard'); }}>{stdOnboardingAlmost ? t('status.almostThere') : t('status.incomplete')}</span>}
                  </button>
                );
              })}
            </div>
            );
          })}
        </nav>
        <div className="sidebar__footer">
          <span className="sidebar__version">build {BUILD_HASH}{BUILD_TIME ? ` · ${BUILD_TIME}` : ''}</span>
        </div>
      </aside>
      )}

      {/* Main */}
      {/* The rail is position: fixed (html/body carry overflow-x: hidden, which
          breaks sticky), so like the left sidebar it reserves its width with a
          margin here rather than taking it as a flex sibling. Full mode
          overlays the page and reserves nothing. */}
      <div className={`main-wrapper ${collapsed ? 'main-wrapper--sidebar-collapsed' : ''} ${location.pathname === '/widget' ? 'main-wrapper--bounded' : ''} ${mobileBlocked ? 'main-wrapper--mobile-locked' : ''} ${railOpen && !mobileBlocked && !railHiddenFor(location.pathname) && railMode !== 'full' ? `main-wrapper--rail-${railMode}` : ''}`}>
        {onboardingStatusLoaded && !showSetupHelp && <AnnouncementBar />}
        {/* Header — hidden on mobile devices. The mobile Conversations skin
            has its own self-contained header (title + 3-dot menu covering
            workspace/online-status/notifications/logout), so this app-shell
            header would just duplicate it (extra bell/account/logo row with
            no purpose once the mobile page owns that surface). */}
        {!mobileBlocked && (
        <header className="header">
          <div className="header__left">
            {/* Hamburger: shown only when the sidebar exists (i.e. NOT a mobile
                device). On mobile there's no sidebar, so it's not rendered. On a
                narrow desktop window it still appears (responsive drawer). */}
            {!mobileBlocked && (
            <button
              className="mobile-hamburger"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('header.openMenu', 'Open menu')}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            )}
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' }}>{t(currentPage.titleKey)}</h1>
          </div>

          <div className="header__center">
            {headerNotification?.type === 'alert' && (
              <div className="header-alert">
                <span className="header-alert__icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
                <span className="header-alert__text">{t('header.needsAssistance', { name: headerNotification.customerName })}</span>
                <button className="header-alert__action" onClick={() => { navigate('/conversations'); setHeaderNotification({ type: 'toast', variant: 'success', message: t('header.jumpedToConversation') }); }}>{t('header.view')}</button>
                <button className="header-alert__dismiss" onClick={() => setHeaderNotification(null)}>×</button>
              </div>
            )}
            {headerNotification?.type === 'assignment' && (
              <div className="header-alert header-alert--assignment">
                <span className="header-alert__icon"><Icons.UserCheck /></span>
                <span className="header-alert__text">{headerNotification.message}</span>
                <button className="header-alert__action header-alert__action--assignment" onClick={() => { navigate('/conversations'); setHeaderNotification({ type: 'toast', variant: 'success', message: t('header.jumpedToAssigned') }); }}>{t('header.view')}</button>
                <button className="header-alert__dismiss header-alert__dismiss--assignment" onClick={() => setHeaderNotification(null)}>×</button>
              </div>
            )}
            {headerNotification?.type === 'toast' && (
              <div className={`header-toast header-toast--${headerNotification.variant || 'info'}`}>
                <span className="header-toast__icon">
                  {headerNotification.variant === 'success' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
                  {headerNotification.variant === 'error' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
                  {(headerNotification.variant === 'info' || !['success', 'error'].includes(headerNotification.variant)) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>}
                </span>
                {headerNotification.message}
                <button className="header-toast__dismiss" onClick={() => setHeaderNotification(null)}>×</button>
              </div>
            )}
          </div>

          <div className="header__right">
            {/* Language toggle */}
            <div ref={langDropdownRef} style={{ position: 'relative' }}>
              <button
                className={`header-icon-btn ${langDropdownOpen ? 'header-icon-btn--active' : ''}`}
                onClick={() => { setLangDropdownOpen(!langDropdownOpen); setLangSearch(''); setAccountDropdownOpen(false); }}
                title={t('header.settings')}
                style={{ fontSize: 16, lineHeight: 1 }}
              >
                {(() => { const lang = supportedUILanguages.find(l => l.code === i18n.language); return lang ? <img src={getFlagUrl(lang.flag)} alt="" width="20" height="14" style={{ verticalAlign: 'middle' }} /> : '🌐'; })()}
              </button>
              {langDropdownOpen && (
                <div className="account-dropdown" style={{ minWidth: 200, padding: '8px 0 4px' }}>
                  <div style={{ padding: '0 8px 6px' }}>
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search language..."
                      value={langSearch}
                      onChange={(e) => setLangSearch(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', fontFamily: 'inherit' }}
                    />
                  </div>
                  {supportedUILanguages.filter(l =>
                    langSearch === '' ||
                    l.name.toLowerCase().includes(langSearch.toLowerCase()) ||
                    l.code.toLowerCase().includes(langSearch.toLowerCase())
                  ).map(lang => (
                    <button
                      key={lang.code}
                      className={`account-dropdown__item ${i18n.language === lang.code ? 'account-dropdown__item--active' : ''}`}
                      onClick={async () => {
                        i18n.changeLanguage(lang.code);
                        setLangDropdownOpen(false);
                        setLangSearch('');
                        try { await updatePreferences({ interfaceLanguage: lang.code }); } catch { /* ignore */ }
                      }}
                      style={i18n.language === lang.code ? { fontWeight: 600, background: '#f0fdf4' } : {}}
                    >
                      <img src={getFlagUrl(lang.flag)} alt="" width="20" height="14" style={{ verticalAlign: 'middle', marginRight: 6 }} />{lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Skill Builder. Opens the rail, which then stays open across
                navigation — the point of the panel is to be readable while
                you do what it tells you. Not offered on Conversations
                (its own three-pane layout) or on mobile. */}
            {!mobileBlocked && !railHiddenFor(location.pathname) && <SkillBuilderLauncher />}

            <NotificationBell liveSignal={notifSignal} />

            <div ref={accountDropdownRef} style={{ position: 'relative' }}>
              <button className={`header-icon-btn ${accountDropdownOpen ? 'header-icon-btn--active' : ''}`} onClick={() => { setAccountDropdownOpen(!accountDropdownOpen); setLangDropdownOpen(false); }} style={{ position: 'relative' }}>
                {userLogo ? (
                  <img 
                    src={userLogo} 
                    alt={userName}
                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <Icons.User />
                )}
                {agentStatus && <span className={`online-dot ${agentStatus === 'offline' ? 'online-dot--offline' : ''}`} />}
              </button>
              {accountDropdownOpen && (
                <div className="account-dropdown">
                  <div className="account-dropdown__user-section">
                    <div className="account-dropdown__user-name">{userName}</div>
                    {agentStatus !== null && (
                      <div className="account-dropdown__status-toggle">
                        <span className="account-dropdown__status-label"><span className={`account-dropdown__status-dot account-dropdown__status-dot--${agentStatus}`} />{agentStatus === 'online' ? t('header.online') : t('header.offline')}</span>
                        <button className={`account-dropdown__toggle account-dropdown__toggle--${agentStatus === 'online' ? 'on' : 'off'}`} onClick={() => { const newStatus = agentStatus === 'online' ? 'offline' : 'online'; setAgentStatus(newStatus); emitAgentStatus(newStatus); }}><span className="account-dropdown__toggle-knob" /></button>
                      </div>
                    )}
                  </div>
                  <button className="account-dropdown__item" disabled={mobileBlocked} onClick={mobileBlocked ? undefined : () => { navigate('/profile'); setAccountDropdownOpen(false); }}><Icons.User /> {t('header.profile')}</button>
                  <button className="account-dropdown__item" disabled={mobileBlocked} onClick={mobileBlocked ? undefined : () => { navigate('/subscription'); setAccountDropdownOpen(false); }}><Icons.CreditCard /> {t('header.billing')}</button>
                  <button className="account-dropdown__item" disabled={mobileBlocked} onClick={mobileBlocked ? undefined : () => { navigate('/settings'); setAccountDropdownOpen(false); }}><Icons.Settings /> {t('header.settings')}</button>
                  <div className="account-dropdown__divider" />
                  <button className="account-dropdown__item account-dropdown__item--danger" onClick={onLogout}><Icons.Logout /> {t('header.logOut')}</button>
                </div>
              )}
            </div>

            <div style={{ width: 1, height: 24, background: '#e5e7eb', margin: '0 4px' }} />

            {/* Single workspace display - no dropdown */}
            {sidebarData && (
              <div className="workspace-switcher" style={{ cursor: 'default' }}>
                {companyLogo ? (
                  <img
                    src={companyLogo}
                    alt={companyName}
                    className="workspace-switcher__avatar"
                    style={{ width: 24, height: 24, borderRadius: 'var(--border-radius-sm)', objectFit: 'cover' }}
                  />
                ) : (
                  <div className="workspace-switcher__avatar" style={{ backgroundColor: '#10b981' }}>
                    {companyName.charAt(0)}
                  </div>
                )}
                <span className="workspace-switcher__name">{companyName}</span>
              </div>
            )}
          </div>
        </header>
        )}

        {/* Integrations detail sub-header — see .subheader in styles/layout.css.
            Sits in the shell rather than inside each page so the back
            affordance is chrome and doesn't scroll away with the content. */}
        {integrationSubheader && (
          <div className="subheader">
            <button
              type="button"
              className="subheader__back"
              onClick={() => navigate('/skills/integrations')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              {tIntegrations('backToIntegrations')}
            </button>
            <span className="subheader__divider" />
            <span className="subheader__current">
              {tIntegrations(integrationSubheader.titleKey)}
            </span>
          </div>
        )}

        {showSetupHelp && <SetupHelpBanner />}

        {/* Usage threshold alert banner (persistent, not auto-dismissing) */}
        {usageAlert && (
          <div className={`usage-alert-banner usage-alert-banner--${usageAlert.data?.threshold === 100 ? 'critical' : usageAlert.data?.threshold === 90 ? 'danger' : 'warning'}`}>
            <span className="usage-alert-banner__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </span>
            <span className="usage-alert-banner__text">
              {t('usageAlert' + (usageAlert.data?.threshold || '80'))}
            </span>
            <button className="usage-alert-banner__action" onClick={() => navigate('/subscription')}>{t('viewUsage')}</button>
            <button className="usage-alert-banner__dismiss" onClick={() => setUsageAlert(null)}>&times;</button>
          </div>
        )}

        <SupportNudge />
        <WidgetHelpLabel />

        {/* Desktop notification permission banner (non-intrusive) */}
        {showDesktopNotifBanner && (
          <div className="desktop-notif-banner">
            <span className="desktop-notif-banner__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </span>
            <span className="desktop-notif-banner__text">{t('desktopNotifBanner')}</span>
            <button className="desktop-notif-banner__enable" onClick={async () => {
              await requestDesktopPermission();
              setShowDesktopNotifBanner(false);
            }}>{t('enable')}</button>
            <button className="desktop-notif-banner__dismiss" onClick={() => {
              setShowDesktopNotifBanner(false);
              const key = 'aerochat_notif_banner_dismissed';
              localStorage.setItem(key, String(parseInt(localStorage.getItem(key) || '0', 10) + 1));
            }}>{t('notNow')}</button>
          </div>
        )}

        <main className="content--no-padding">
          <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '60vh', color: '#6b7280' }}>{t('status.loading')}</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/conversations" element={<PermGate permission="conversations.view" sidebarData={sidebarData}>{mobileBlocked ? <MobileConversationsPage agentStatus={agentStatus} onToggleAgentStatus={(next) => { setAgentStatus(next); emitAgentStatus(next); }} companyName={companyName} onLogout={onLogout} /> : <ConversationsPage onNotification={setHeaderNotification} />}</PermGate>} />
            {/* Mobile-only — desktop keeps the NotificationBell dropdown for this. */}
            <Route path="/conversations/notifications" element={mobileBlocked ? <PermGate permission="conversations.view" sidebarData={sidebarData}><MobileNotificationsPage headerMenuProps={{ agentStatus, onToggleAgentStatus: (next) => { setAgentStatus(next); emitAgentStatus(next); }, companyName, onLogout }} /></PermGate> : <Navigate to="/conversations" replace />} />
            <Route path="/widget" element={<WidgetAppearancePage widgetConfig={widgetConfig} setWidgetConfig={setWidgetConfig} onNotification={setHeaderNotification} onBrandUpdate={({ name, logo }) => setSidebarData(prev => prev ? { ...prev, ...(name !== undefined && { company_name: name }), ...(logo !== undefined && { company_logo: logo }) } : prev)} />} />
            <Route path="/channels" element={<PermGate permission="channels.view" sidebarData={sidebarData}><ChannelsPage onNotification={setHeaderNotification} platformType={currentUser?.platformType} sidebarData={sidebarData} /></PermGate>} />
            {/* Revenue-from-AI hero card is gated exactly like the Conversions page: conversion_tracking entitlement (tier 3/4) AND a connected Shopify store. */}
            <Route path="/dashboard" element={<Dashboard onNotification={setHeaderNotification} showRevenueCard={canAccess('conversion_tracking') && shopifyConnected} />} />
            {/* Conversions — nav entry under AI. Gated to plans with the conversion_tracking entitlement (AppSumo tier 3 & 4, growth/advanced/enterprise) AND a connected Shopify store, since it's a Shopify-only feature. */}
            <Route path="/link-conversions" element={<EntGate entitlement="conversion_tracking" sidebarData={sidebarData}>{!shopifyConnChecked ? null : shopifyConnected ? <LinkConversionsPage /> : <Navigate to="/dashboard" replace />}</EntGate>} />
            <Route path="/test-bot" element={<TestYourBotPage widgetConfig={widgetConfig} />} />
            {/* Shopify onboarding now lives in the embedded Shopify app — the
                main app never shows it; Shopify merchants are sent to the
                dashboard. (Non-Shopify onboarding routes below are unchanged.) */}
            <Route path="/getstarted/shopify" element={shopifyConnected ? <Navigate to="/dashboard" replace /> : <OnboardingPage items={onboardingItems} setItems={setOnboardingItems} loading={onboardingLoading} />} />
            <Route path="/getstarted/standard" element={<StandardOnboardingPage items={standardOnboardingItems} setItems={setStandardOnboardingItems} onNavigate={(page) => navigate(`/${page}`)} loading={standardOnboardingLoading} />} />
            <Route path="/getstarted/wordpress" element={<WordPressOnboardingPage items={wpOnboardingItems} setItems={setWpOnboardingItems} onNavigate={(page) => navigate(`/${page}`)} loading={wpOnboardingLoading} />} />
            {/* Redirect old URLs */}
            <Route path="/onboarding" element={<Navigate to="/getstarted/shopify" replace />} />
            <Route path="/standard-onboarding" element={<Navigate to="/getstarted/standard" replace />} />
            
             <Route path="/knowledge-base" element={<PermGate permission="knowledge_base.view" sidebarData={sidebarData}><KnowledgeBasePage onNotification={setHeaderNotification} shopifyConnected={shopifyConnected} wordpressConnected={wordpressConnected} wixConnected={wixConnected} tiktokShopConnected={tiktokShopConnected} platformType={currentUser?.platformType} connectedPlatform={(currentUser?.platformType === 'wordpress' || wordpressConnected) ? 'WordPress' : (wixConnected && !shopifyConnected) ? 'Wix' : 'Shopify'} /></PermGate>} />
            <Route path="/custom-instructions" element={<PermGate permission="custom_instructions.view" sidebarData={sidebarData}><CustomInstructionsPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/playbooks" element={<PermGate permission="playbooks.view" sidebarData={sidebarData}><PlaybooksPage onNotification={setHeaderNotification} /></PermGate>} />
            {/* AI Tools — the Skills runtime (tenant-authored skill markdown,
                custom tools, credentials) and the Integrations hub, as two
                tabs of one sidebar entry. No PermGate here: the page gates
                each TAB itself (skills.view — added in migration
                037_skill_runtime.sql — and integrations.view) and forwards to
                the one the merchant can see, because the sidebar always links
                to /skills. Owner/Admin bypass the gate per
                app/auth/deps.py::require_permission. */}
            <Route path="/skills" element={<AiToolsPage onNotification={setHeaderNotification} sidebarData={sidebarData} />} />
            <Route path="/skills/integrations" element={<AiToolsPage onNotification={setHeaderNotification} sidebarData={sidebarData} />} />
            <Route path="/shopify"  element={onboardingLoading ? null : (shopifyConnected ? <ShopifyPage setupComplete={setupComplete} shopifyConnected={shopifyConnected} onNotification={setHeaderNotification} /> : <Navigate to="/dashboard" replace />)}  />
            <Route path="/magento" element={<PermGate permission="channels.configure" sidebarData={sidebarData}><MagentoPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/wix" element={<PermGate permission="channels.configure" sidebarData={sidebarData}><WixPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/google-sheets" element={<PermGate permission="channels.configure" sidebarData={sidebarData}><GoogleSheetsPage onNotification={setHeaderNotification} /></PermGate>} />
            {/* Legacy URL — the Integrations hub is now a tab of AI Tools. */}
            <Route path="/integrations" element={<Navigate to="/skills/integrations" replace />} />
            <Route path="/integrations/webhooks" element={<PermGate permission="integrations.view" sidebarData={sidebarData}><WebhooksPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/integrations/zapier" element={<PermGate permission="integrations.view" sidebarData={sidebarData}><ZapierPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/integrations/mcp" element={<PermGate permission="integrations.view" sidebarData={sidebarData}><MCPPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/integrations/developer-api" element={<PermGate permission="integrations.view" sidebarData={sidebarData}><DeveloperApiPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/integrations/airwallex" element={<PermGate permission="integrations.view" sidebarData={sidebarData}><AirwallexPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/integrations/pos-malaysia" element={<PermGate permission="integrations.view" sidebarData={sidebarData}><PosMalaysiaPage onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/self-learning" element={<EntGate entitlement="self_learning_ai" sidebarData={sidebarData}><PermGate permission="self_learning.view" sidebarData={sidebarData}><SelfLearningContainer onNotification={setHeaderNotification} /></PermGate></EntGate>} />
            <Route path="/wordpress" element={isWordpress ? <PermGate permission="channels.configure" sidebarData={sidebarData}><WordPressPage onNotification={setHeaderNotification} /></PermGate> : <Navigate to="/dashboard" replace />} />
            <Route path="/settings" element={<PermGate permission="settings.view" sidebarData={sidebarData}><SettingsPage onNotification={setHeaderNotification} onCompanyUpdate={({ name }) => setSidebarData(prev => prev ? { ...prev, company_name: name } : prev)} sidebarData={sidebarData} /></PermGate>} />
            <Route path="/settings/roles" element={<PermGate permission="settings.view" sidebarData={sidebarData}><RolesPermissionsPage onNotification={setHeaderNotification} sidebarData={sidebarData} /></PermGate>} />
            <Route path="/broadcasts" element={<PermGate permission="broadcasts.view" sidebarData={sidebarData}><BroadcastsPage /></PermGate>} />
            <Route path="/broadcasts/new" element={<PermGate permission="broadcasts.view" sidebarData={sidebarData}><BroadcastWizardPage /></PermGate>} />
            <Route path="/broadcasts/:id/edit" element={<PermGate permission="broadcasts.view" sidebarData={sidebarData}><BroadcastWizardPage /></PermGate>} />
            <Route path="/broadcasts/:id" element={<PermGate permission="broadcasts.view" sidebarData={sidebarData}><BroadcastDetailPage /></PermGate>} />
            <Route path="/templates" element={<PermGate permission="broadcasts.view" sidebarData={sidebarData}><TemplateListPage /></PermGate>} />
            <Route path="/templates/new" element={<PermGate permission="broadcasts.view" sidebarData={sidebarData}><TemplateCreationPage /></PermGate>} />
            <Route path="/segments" element={<SegmentManagementPage />} />
            <Route path="/contacts" element={<PermGate permission="contacts.view" sidebarData={sidebarData}><ContactsPage planId={sidebarData?.plan_id || 'free'} onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/contacts/import" element={<PermGate permission="contacts.edit" sidebarData={sidebarData}><ContactImportWizardPage /></PermGate>} />

            <Route path="/subscription" element={<PermGate permission="billing.view" sidebarData={sidebarData}><PricingPage sidebarData={sidebarData} onNotification={setHeaderNotification} /></PermGate>} />
            <Route path="/profile" element={<ProfilePage onNotification={setHeaderNotification} onNotifPrefsChange={setNotifPrefs} onUserUpdate={({ avatar, name }) => setSidebarData(prev => prev ? { ...prev, ...(avatar && { user_logo: avatar }), ...(name && { user_name: name }) } : prev)} />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </main>
      </div>

      {/* Last flex child of .admin-layout: in its docked and wide modes the
          rail takes width from .main-wrapper (flex: 1; min-width: 0) rather
          than covering it, so the page stays usable beside it. */}
      <SkillBuilderRail />
    </div>
  );
}